import { availableBalance } from '../../walletFigures';
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./clientBookingDetails.css";
import { DateTime } from 'luxon';
import ClientBookingReports from "./ClientBookingReports";
import ClientBookingTasks from "./ClientBookingTasks";
import ClientBookingLaundry from "./ClientBookingLaundry";
import ClientBookingFeesSection from "./ClientBookingFeesSection";
import ClientWalletCharges from "./ClientWalletCharges";
import ClientPriceChanges from "./ClientPriceChanges";
import RequestBookingCancelModal from "../../ClientRequests/RequestBookingCancelModal";
import useClientRequestsEnabled from "../../ClientRequests/useClientRequestsEnabled";
import { formatDubai } from '../../../../utils/dubaiTime';

const ClientBookingDetails = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    // Card payments are RECEIPTS now (payments restructure Phase 2). The
    // "Paid online · Card" chip is derived from these, not the old
    // booking.online_payment band-aid.
    const [cardReceipts, setCardReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isLaundryMatching, setIsLaundryMatching] = useState(false);
    // Change / cancellation requests (client-requests feature)
    const requestsEnabled = useClientRequestsEnabled();
    const [pendingRequest, setPendingRequest] = useState(null); // full pending edit/cancel request row
    const [walletSummary, setWalletSummary] = useState(null); // balance context for the pending panel
    const [withdrawing, setWithdrawing] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    // Bumped when an adjustment settles so the Wallet Charges card refetches.
    // Price adjustments RETIRED (2026-07-21) — nothing remounts the
    // Transactions card any more (there is no in-page adjustment payment to
    // settle), so the key is a stable constant.
    const [walletChargesKey] = useState(0);

    // Does this booking already have an open change/cancellation request?
    const fetchPendingRequest = useCallback(async () => {
        if (!requestsEnabled) { setPendingRequest(null); return; }
        try {
            const res = await axios.get('/api/client-requests/requests/my', { params: { status: 'pending' } });
            const rows = Array.isArray(res.data?.requests) ? res.data.requests : [];
            const match = rows.find(
                (r) => (r.request_type === 'edit_booking' || r.request_type === 'cancel_booking')
                    && String(r.target_id) === String(bookingId)
            );
            setPendingRequest(match || null);
            // Balance context for the pending panel (covers-the-change indicator)
            if (match) {
                try {
                    const w = await axios.get('/api/client-requests/wallet');
                    setWalletSummary(w.data || null);
                } catch (wErr) {
                    setWalletSummary(null);
                }
            }
        } catch (err) {
            if (err.response?.status !== 503) console.error('Error checking pending requests:', err);
            setPendingRequest(null);
        }
    }, [bookingId, requestsEnabled]);

    const withdrawRequest = async () => {
        if (!pendingRequest || withdrawing) return;
        if (!window.confirm('Withdraw this request?')) return;
        setWithdrawing(true);
        try {
            await axios.post(`/api/client-requests/requests/${pendingRequest.id}/cancel`);
            await fetchPendingRequest();
        } catch (err) {
            console.error('Error withdrawing request:', err);
            // e.g. staff approved it moments ago — say so, then refresh to reality
            window.alert(err.response?.data?.error || 'Could not withdraw this request. Please refresh and try again.');
            await fetchPendingRequest();
        } finally {
            setWithdrawing(false);
        }
    };

    useEffect(() => {
        fetchPendingRequest();
    }, [fetchPendingRequest]);

    useEffect(() => {
        const fetchBookingDetails = async () => {
            try {
                const response = await axios.get(`/api/client-requests/bookings/${bookingId}`);
                setBooking(response.data);
            } catch (err) {
                setError("Failed to fetch booking details.");
            } finally {
                setLoading(false);
            }
        };

        const checkLaundryMatch = async () => {
            try {
                const response = await axios.get(`/api/client-requests/bookings/laundry-check/${bookingId}`);
                if (response.data.visible) {
                    setIsLaundryMatching(true);
                }
            } catch (err) {
                console.error("Laundry check failed", err);
            }
        };

        const fetchCardReceipts = async () => {
            try {
                const res = await axios.get(`/api/client-requests/wallet/bookings/${bookingId}/transactions`);
                setCardReceipts(Array.isArray(res.data) ? [] : (res.data?.receipts || []));
            } catch {
                setCardReceipts([]);
            }
        };

        checkLaundryMatch();
        fetchBookingDetails();
        fetchCardReceipts();
    }, [bookingId]);

    if (loading) return (
        <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading Booking Details...</div>
        </div>
    );
    if (error) return <div className="cbd__error">{error}</div>;

    const formatDate = (dateString) => {
        const dt = DateTime.fromISO(dateString, { zone: 'Asia/Dubai' });
        return dt.toFormat("dd LLL yyyy, HH:mm");
    };

    const statusClass = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'confirmed' || s === 'completed') return 'cbd__statusChip--green';
        if (s === 'pending') return 'cbd__statusChip--orange';
        if (s.includes('cancel') || s.includes('reschedule')) return 'cbd__statusChip--red';
        if (s === 'in process') return 'cbd__statusChip--blue';
        return '';
    };

    return (
        <div className="cbd">
            {/* Header */}
            <div className="cbd__header">
                <Link to="/client/bookings" className="cbd__back">← Back to Bookings</Link>
                <div className="cbd__headerMain">
                    <div>
                        <h1 className="cbd__title">Booking #{booking.booking_id}</h1>
                        <div className="cbd__subtitle">
                            {booking.unit_number ? `Unit ${booking.unit_number} · ${booking.building_name}` : booking.building_name}
                        </div>
                        <div className="cbd__statusRow">
                            <span className={`cbd__statusChip ${statusClass(booking.booking_status)}`}>
                                {booking.booking_status}
                            </span>
                            {/* Card-paid marker: derived from booking payment
                                RECEIPTS (payments restructure Phase 2). */}
                            {cardReceipts.length > 0 && (() => {
                                const cardTotal = cardReceipts.reduce((s, r) => s + Number(r.amount || 0), 0);
                                const orderRef = cardReceipts.find((r) => r.gateway_order_ref)?.gateway_order_ref;
                                return (
                                    <span
                                        className="cbd__paidOnlineChip"
                                        title={orderRef ? `Order ref: ${orderRef}` : undefined}
                                    >
                                        {/* P1 — a booking payment, not a top-up.
                                            The wallet was never involved. */}
                                        Booking payment (card) · AED {cardTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Top-right: change / cancellation actions. Only for future,
                        uninvoiced, client-manageable bookings (type 1/2/4) in a
                        changeable state — the server re-validates everything. */}
                    {requestsEnabled && !pendingRequest && booking.future_ok === true && booking.invoice_id == null
                        && [1, 2, 4].includes(booking.type_id)
                        && ['confirmed', 'pending'].includes(String(booking.booking_status || '').toLowerCase()) && (
                            <div className="cbd__requestActions">

                                <button type="button" className="cbd__requestBtn cbd__requestBtn--danger" onClick={() => setShowCancelModal(true)}>
                                    Cancel Booking
                                </button>
                                {/* can_client_edit === false → no Edit button, with the
                                reason stated. `=== false` on purpose: a server
                                that doesn't send the flag must keep today's
                                behaviour rather than silently losing Edit. */}
                                {String(booking.booking_status || '').toLowerCase() === 'confirmed' && (
                                    booking.can_client_edit === false ? (
                                        <span className="cbd__requestNote" role="note">
                                            {booking.client_edit_reason
                                                || 'This booking can only be changed by our team — please contact us.'}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className="cbd__requestBtn"
                                            /* B1: Edit is a PAGE now — deep-linkable, and
                                               the paypage round-trips return to it. */
                                            onClick={() => navigate(`/client/bookings/${booking.booking_id}/edit-request`)}
                                        >
                                            Edit Booking
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                </div>
            </div>

            {/* Pending request panel — spells out exactly WHAT was requested */}
            {pendingRequest && (() => {
                const p = pendingRequest.payload || {};
                const isCancel = pendingRequest.request_type === 'cancel_booking';
                const changed = Array.isArray(p.changed_fields) ? p.changed_fields : [];
                // How this change is funded, from the request itself — not an
                // assumption. 'awaiting_payment' means its card payment never
                // completed, so nothing has been charged and nothing is reserved.
                const cardSettled = String(p.settlement?.mode || '') === 'card'
                    || String(p.funding?.rail || '').includes('card');
                const awaitingPayment = pendingRequest.status === 'awaiting_payment';
                return (
                    <div className="cbd__pendingPanel">
                        <div className="cbd__pendingPanelHead">
                            <span className="cbd__requestChip">
                                {isCancel ? 'Cancellation requested' : 'Change requested'}
                            </span>
                            <span className="cbd__pendingPanelSub">Awaiting our team&apos;s review</span>
                            <button
                                type="button"
                                className="cbd__pendingWithdraw"
                                onClick={withdrawRequest}
                                disabled={withdrawing}
                            >
                                {withdrawing ? 'Withdrawing…' : 'Withdraw request'}
                            </button>
                        </div>

                        {isCancel ? (
                            <div className="cbd__pendingRows">
                                <div className="cbd__pendingRow">
                                    <span>Requested</span>
                                    <strong>Cancel this booking</strong>
                                </div>
                                <div className="cbd__pendingRow">
                                    <span>If approved</span>
                                    <strong>
                                        {p.is_last_minute_at_request
                                            ? (() => {
                                                // Signed LM math (mirror the cancel modal): the fee nets
                                                // against what was actually charged — it can refund the
                                                // remainder OR charge the shortfall, not always refund.
                                                const net = Number(p.net_charged) || 0;
                                                const fee = Number(p.penalty_fee_incl_vat) || 0;
                                                const diff = Math.round((net - fee) * 100) / 100;
                                                if (diff > 0.01) return `Last-minute fee of AED ${fee.toFixed(2)} applies — AED ${diff.toFixed(2)} refunded to your wallet`;
                                                if (diff < -0.01) return `Last-minute fee of AED ${fee.toFixed(2)} applies — AED ${Math.abs(diff).toFixed(2)} charged from your wallet`;
                                                return `Last-minute fee of AED ${fee.toFixed(2)} applies — what you paid covers it exactly`;
                                            })()
                                            : (Number(p.net_charged) > 0
                                                ? `Booking removed · AED ${Number(p.net_charged).toFixed(2)} refunded to your wallet`
                                                : 'Booking removed · nothing was charged')}
                                    </strong>
                                </div>
                                {p.reason && (
                                    <div className="cbd__pendingRow">
                                        <span>Your reason</span>
                                        <strong>{p.reason}</strong>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="cbd__pendingRows">
                                {changed.includes('type_id') && (
                                    <div className="cbd__pendingRow">
                                        <span>New service</span>
                                        <strong>{p.type_label || '—'}</strong>
                                    </div>
                                )}
                                {changed.includes('booking_start') && (
                                    <div className="cbd__pendingRow">
                                        <span>New time</span>
                                        <strong>{formatDubai(p.booking_start, 'd MMM yyyy, HH:mm') || ''}</strong>
                                    </div>
                                )}
                                {changed.includes('duration_hours') && (
                                    <div className="cbd__pendingRow">
                                        <span>New duration</span>
                                        <strong>{p.duration_hours} hrs</strong>
                                    </div>
                                )}
                                {changed.includes('required_cleaners') && (
                                    <div className="cbd__pendingRow">
                                        <span>New cleaners</span>
                                        <strong>{p.required_cleaners}</strong>
                                    </div>
                                )}
                                {changed.includes('notes') && (
                                    <div className="cbd__pendingRow">
                                        <span>New notes</span>
                                        <strong>{p.notes || '(cleared)'}</strong>
                                    </div>
                                )}
                                {/* NAME THE RAIL THIS CHANGE IS ACTUALLY FUNDED BY.
                                    A card-settled change said "from your wallet"
                                    whatever it was paid with, which is wrong twice
                                    over when the card payment has not happened yet:
                                    the client is told money will leave a wallet they
                                    may have deliberately excluded, and nothing says
                                    the change is waiting on a payment. */}
                                {Number(p.price_delta) !== 0 && Number.isFinite(Number(p.price_delta)) && (
                                    <div className="cbd__pendingRow">
                                        <span>Price impact if approved</span>
                                        <strong>
                                            {Number(p.price_delta) > 0
                                                ? `+AED ${Number(p.price_delta).toFixed(2)} ${cardSettled ? 'by card' : 'from your wallet'}`
                                                : `−AED ${Math.abs(Number(p.price_delta)).toFixed(2)} refunded to your wallet`}
                                        </strong>
                                    </div>
                                )}
                                {awaitingPayment && (
                                    <div className="cbd__pendingRow">
                                        <span>Payment</span>
                                        <strong style={{ color: '#b45309' }}>
                                            Not paid yet — this change is only submitted once the card
                                            payment goes through. Withdraw it and request the change again
                                            to pay.
                                        </strong>
                                    </div>
                                )}
                                {/* Reads availableBalance — the ONE wallet figure (total −
                                    reserved − payouts). The reservation for THIS change is
                                    already included in it, so ≥ 0 means every pending
                                    request (incl. this one) is covered. Due adjustments
                                    never gate this sentence: they are a separate obligation,
                                    not a hold on the wallet (2026-07-19). */}
                                {walletSummary && !cardSettled && Number(p.price_delta) > 0.01 && (
                                    <div className="cbd__pendingRow">
                                        <span>Your balance</span>
                                        <strong style={{ color: availableBalance(walletSummary) >= -0.01 ? '#047857' : '#b91c1c' }}>
                                            {availableBalance(walletSummary) >= -0.01
                                                ? `Covered — AED ${availableBalance(walletSummary).toFixed(2)} left after all pending requests`
                                                : `Short AED ${Math.abs(availableBalance(walletSummary)).toFixed(2)} — please top up before our team approves`}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* The edit MODAL that mounted here is a PAGE now (B1) — the Edit
                Booking button above navigates to
                /client/bookings/:id/edit-request. The cancel flow stays a
                modal on this page. */}
            {showCancelModal && (
                <RequestBookingCancelModal
                    booking={booking}
                    onClose={() => setShowCancelModal(false)}
                    onSubmitted={fetchPendingRequest}
                />
            )}

            {/* Info grid */}
            <div className="cbd__card">
                <div className="cbd__infoGrid">
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Date & Time</div>
                        <div className="cbd__infoValue">{formatDate(booking.booking_start)}</div>
                    </div>
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Duration</div>
                        <div className="cbd__infoValue">{booking.duration_hours} hours</div>
                    </div>
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Type</div>
                        <div className="cbd__infoValue">{booking.booking_type}</div>
                    </div>
                    <div className="cbd__infoItem">
                        <div className="cbd__infoLabel">Amount (incl. VAT)</div>
                        <div className="cbd__infoValue cbd__infoValue--accent">
                            AED {Number(booking.total_with_vat || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tasks */}
            <div className="cbd__card">
                <h3 className="cbd__cardTitle">Tasks</h3>
                <ClientBookingTasks
                    bookingId={bookingId}
                    canManageTasks={
                        requestsEnabled && !pendingRequest
                        && booking.future_ok === true
                        && booking.invoice_id == null
                        && String(booking.booking_status || '').toLowerCase() === 'confirmed'
                    }
                    onSubmitted={fetchPendingRequest}
                />
            </div>

            {/* Other reports — task reports live under their task in the Tasks card */}
            <div className="cbd__card">
                <h3 className="cbd__cardTitle">Other Reports</h3>
                <ClientBookingReports bookingId={bookingId} />
            </div>

            {/* Linens */}
            {isLaundryMatching && (
                <div className="cbd__card">
                    <h3 className="cbd__cardTitle">Linens</h3>
                    <ClientBookingLaundry bookingId={bookingId} />
                </div>
            )}

            {/* Fees */}
            <div className="cbd__card">
                <h3 className="cbd__cardTitle">Booking Fees</h3>
                <ClientBookingFeesSection
                    bookingId={bookingId}
                    title="Booking Fees"
                    compact={false}
                    initialTotals={{
                        subtotal_amount: booking.subtotal_amount,
                        vat_amount: booking.vat_amount,
                        total_with_vat: booking.total_with_vat,
                    }}
                />
            </div>

            {/* Wallet Charges */}
            <ClientWalletCharges key={walletChargesKey} bookingId={bookingId} />

            {/* Price changes + pending on this booking (owner 2026-07-21 —
                replaces price adjustments). Sits directly UNDER Transactions,
                same card design. Renders nothing when there are none. */}
            <ClientPriceChanges bookingId={bookingId} />

            {/* Notes (at the very bottom) */}
            {(booking.property_permanent_notes || booking.client_permanent_notes || booking.booking_specific_notes) && (
                <div className="cbd__card">
                    <h3 className="cbd__cardTitle">Notes</h3>
                    {(booking.property_permanent_notes || booking.client_permanent_notes) && (
                        <div className="cbd__notesBlock">
                            <div className="cbd__notesLabel">Permanent Notes</div>
                            <pre className="cbd__notesPre">
                                {booking.property_permanent_notes}
                                {booking.property_permanent_notes && booking.client_permanent_notes ? '\n\n' : ''}
                                {booking.client_permanent_notes}
                            </pre>
                        </div>
                    )}
                    {booking.booking_specific_notes && (
                        <div className="cbd__notesBlock">
                            <div className="cbd__notesLabel">Booking Notes</div>
                            <div className="cbd__notesText">{booking.booking_specific_notes}</div>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default ClientBookingDetails;
